import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  ArtifactType,
  ComplianceProgramState,
  GetComplianceProgramLoadPreviewQuery,
  GetComplianceProgramLoadPreviewQueryVariables,
} from "types/graphql-schema";

import getComplianceProgramLoadPreviewQuery from "@apps/programs/graphql/get-compliance-program-load-preview-query";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";

import { convertProgramFormToMutationVariables } from "../form-data";
import { ProgramForm } from "../types";
import { missingSelectionField } from "../use-program-form-timeline";
import {
  LoadPreview,
  WriterGroup,
  defaultSecondsPerQuestion,
  formatCount,
  formatDuration,
  groupLabel,
  groupWriters,
  heaviestLabel,
  largestGroup,
  mixText,
  plural,
  secondsPerQuestionOptions,
} from "./readiness-figures";
import ReadinessIntegrations from "./readiness-integrations";

/** More bars than this stop reading as a shape and start reading as a list. */
const maxBars = 8;

const QuestionTile = ({
  questions,
  label,
  reviews,
}: {
  questions: number;
  label: string;
  reviews?: string;
}) => (
  <div className="rounded-md border px-4 py-3">
    <div className="text-2xl font-semibold tabular-nums">
      {formatCount(questions)}
    </div>
    <div className="text-sm text-gray-500">
      {label}
      {reviews !== undefined && (
        <span className="text-gray-400"> ({reviews})</span>
      )}
    </div>
  </div>
);

const LoadTile = ({
  eyebrow,
  title,
  group,
  seconds,
  heavy,
}: {
  eyebrow?: string;
  title: string;
  group: WriterGroup;
  seconds: number;
  heavy?: boolean;
}) => {
  const label = useLabel();
  return (
    <div className="rounded-md border px-4 py-3">
      {eyebrow && <div className="text-xs text-gray-400">{eyebrow}</div>}
      <div className="truncate text-sm text-gray-500">{title}</div>
      <div
        className={`text-2xl font-semibold tabular-nums ${heavy ? "text-red-700" : ""}`}
      >
        {formatDuration(group.questions * seconds)}
      </div>
      <div className="text-sm text-gray-500 tabular-nums">
        {plural(group.questions, "question")} · {mixText(group.byStep, label)}
      </div>
    </div>
  );
};

const barColour = (index: number, share: number, alone: boolean) => {
  if (index === 0 && !alone) {
    return "bg-red-500";
  }
  return share >= 0.55 && !alone ? "bg-amber-400" : "bg-blue-500";
};

const Distribution = ({
  groups,
  seconds,
}: {
  groups: WriterGroup[];
  seconds: number;
}) => {
  const label = useLabel();
  const heaviest = groups[0].questions;
  const shown = groups.slice(0, maxBars);
  const hidden = groups.length - shown.length;
  const alone = groups.length === 1;

  return (
    <ul className="space-y-1.5">
      {shown.map((group, index) => {
        const share = heaviest > 0 ? group.questions / heaviest : 0;
        return (
          <li
            key={group.key}
            className="flex items-center gap-3 text-sm"
            title={mixText(group.byStep, label)}
          >
            <span className="w-32 shrink-0 truncate">{groupLabel(group)}</span>
            <div className="h-2 flex-1 rounded-full bg-gray-100">
              <div
                className={`h-2 rounded-full ${barColour(index, share, alone)}`}
                style={{ width: `${Math.max(share * 100, 4)}%` }}
              />
            </div>
            <span className="w-20 shrink-0 text-right text-gray-500 tabular-nums">
              {formatDuration(group.questions * seconds)}
            </span>
          </li>
        );
      })}
      {hidden > 0 && (
        <li className="text-sm text-gray-500">
          + {plural(hidden, "more group")}
        </li>
      )}
    </ul>
  );
};

const LoadFigures = ({
  preview,
  noun,
  seconds,
  onChangeSeconds,
}: {
  preview: LoadPreview;
  noun: string;
  seconds: number;
  onChangeSeconds: (seconds: number) => void;
}) => {
  const label = useLabel();
  const groups = useMemo(
    () => groupWriters(preview.writers),
    [preview.writers],
  );
  const typical = largestGroup(groups);
  const busiest = preview.writers[0];
  if (!typical || !busiest) {
    return null;
  }
  const heaviest = groups[0];
  const lightest = groups[groups.length - 1];
  const reviewCount = (count: number) =>
    `${formatCount(count)} ${label("review", { pluralize: count })}`;

  return (
    <div className="space-y-5 pt-3">
      <div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <QuestionTile
            questions={preview.questionsPerReview}
            label={`questions in one ${label("review")}`}
          />
          <QuestionTile
            questions={typical.questions}
            // The largest group, which need not be a majority: default peers
            // split people by the size of their team. So it says how many.
            label={`questions each for ${plural(typical.writers.length, "person", "people")}`}
            reviews={reviewCount(typical.reviews)}
          />
          <QuestionTile
            questions={busiest.questions}
            label={`questions for ${heaviestLabel(heaviest)}`}
            reviews={reviewCount(busiest.reviews)}
          />
        </div>
        {/* One note for all three numbers rather than "at least" on each: the
            backend cannot count the questions a review adds per person. */}
        <div className="mt-2 text-sm text-gray-500">
          Role, {label(ArtifactType.Goal)} and core value questions are added
          for each person when the {noun} starts, so these are the least anyone
          answers.
        </div>
      </div>

      <div className="text-sm">
        Assume{" "}
        <select
          aria-label="Time per question"
          className="rounded-md border-gray-300 py-0.5 pl-2 pr-8 text-sm"
          value={seconds}
          onChange={(event) => onChangeSeconds(Number(event.target.value))}
        >
          {secondsPerQuestionOptions.map((option) => (
            <option key={option.seconds} value={option.seconds}>
              {option.label}
            </option>
          ))}
        </select>{" "}
        per question.
      </div>

      {groups.length === 1 ? (
        <LoadTile
          title="What everyone writes"
          group={heaviest}
          seconds={seconds}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LoadTile
            eyebrow="Lightest"
            title={groupLabel(lightest)}
            group={lightest}
            seconds={seconds}
          />
          <LoadTile
            eyebrow="Heaviest"
            title={heaviestLabel(heaviest)}
            group={heaviest}
            seconds={seconds}
            heavy
          />
        </div>
      )}

      <Distribution groups={groups} seconds={seconds} />

      <div className="text-sm text-gray-500">
        {reviewCount(preview.totalReviews)} and{" "}
        {plural(preview.totalAnswers, "answer")} across the company — about{" "}
        {formatDuration(preview.totalAnswers * seconds)} of working time.
      </div>
    </div>
  );
};

/**
 * How much work the review on this form will ask of people, before it is
 * published: how long one review is, what the largest group writes, what the busiest
 * person writes, and how the rest spread between the two.
 *
 * Runs as soon as the tab is shown, with no button to open it: choosing this tab
 * is the admin asking the question.
 *
 * Nothing here saves. The query takes the form's current values as arguments and
 * hands back an answer; the review is written only when the admin saves the form.
 * The time per question is a guess the admin makes on the screen, so changing it
 * multiplies the answer already in hand instead of asking again.
 */
const ProgramFormReadiness = ({
  proposedComplianceProgram,
}: {
  proposedComplianceProgram: ProgramForm;
}) => {
  const { noun } = useProgramsSection();
  const [secondsPerQuestion, setSecondsPerQuestion] = useState(
    defaultSecondsPerQuestion,
  );

  // A running review froze its roster and locked its templates, so it is counted
  // as the Timeline panel plans it: from the saved roster and the saved templates.
  const isRunning =
    proposedComplianceProgram.id !== undefined &&
    proposedComplianceProgram.state !== ComplianceProgramState.Draft;

  // The same variables as the participants preview, so both ask about the same
  // unsaved form.
  const variables =
    useMemo<GetComplianceProgramLoadPreviewQueryVariables>(() => {
      const saved = convertProgramFormToMutationVariables(
        proposedComplianceProgram,
        !isRunning,
      );
      return {
        organizationId: proposedComplianceProgram.organizationId,
        complianceProgramId: proposedComplianceProgram.id,
        steps: saved.steps,
        assessmentTemplates: saved.assessmentTemplates,
        calibration: saved.calibration,
        ongoing: saved.ongoing,
        startDateOffset: saved.startDateOffset,
        appliesTo: saved.appliesTo,
        appliesToTeams: saved.appliesToTeams,
        appliesToManagers: saved.appliesToManagers,
        appliesToUsers: saved.appliesToUsers,
        excludedUsers: saved.excludedUsers,
        excludedHireDateAfter: saved.excludedHireDateAfter,
        removeExcludedHireDateAfter: saved.removeExcludedHireDateAfter,
        savedRoster: isRunning,
      };
    }, [proposedComplianceProgram, isRunning]);

  // Debounced on the values rather than on the object, because every keystroke
  // anywhere in the form hands this component a new object with the same values.
  const liveKey = JSON.stringify(variables);
  const debouncedKey = useDebounce(liveKey, 500);
  const debouncedVariables = useMemo(
    () =>
      JSON.parse(debouncedKey) as GetComplianceProgramLoadPreviewQueryVariables,
    [debouncedKey],
  );

  // A rule that names nobody yet has nothing to resolve, so it is answered here
  // instead of by the backend. A running review never waits: it counts its
  // frozen roster, not the rule.
  const missingField =
    missingSelectionField[proposedComplianceProgram.appliesTo];
  const waitingForASelection = Boolean(
    !isRunning &&
      missingField &&
      (proposedComplianceProgram[missingField] as unknown[]).length === 0,
  );

  const { data, loading, error } = useQuery<
    GetComplianceProgramLoadPreviewQuery,
    GetComplianceProgramLoadPreviewQueryVariables
  >(getComplianceProgramLoadPreviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: debouncedVariables,
    skip: waitingForASelection,
  });

  // Apollo drops the data it holds the moment the variables change, so the last
  // answer is kept here: it is what stays on screen while the next one is being
  // fetched, instead of the figures blinking out on every edit.
  const [remembered, setRemembered] = useState<LoadPreview | undefined>(
    undefined,
  );
  const fromServer = data?.complianceProgramLoadPreview;
  useEffect(() => {
    if (fromServer) {
      setRemembered(fromServer);
    }
  }, [fromServer]);
  // The backend's own words when it has any: a rule reaching outside the admin's
  // tree is refused with a sentence saying what to change.
  const refusal = error?.graphQLErrors?.[0]?.message ?? null;
  // A refusal is about the selection on screen, so the figures for an earlier
  // selection must not stay under it.
  const preview = fromServer ?? (refusal ? undefined : remembered);

  return (
    <div className="rounded-md border p-6">
      <div className="flex items-baseline gap-2">
        <span className="tf-header-base-dark">
          How heavy is this {noun.toLowerCase()}?
        </span>
        {!waitingForASelection && loading && preview && (
          <Loading size="4" mini />
        )}
      </div>

      {waitingForASelection ? (
        <div className="pt-3 text-sm text-gray-500">
          Choose who takes part on the Participants tab first.
        </div>
      ) : (
        <>
          {/* A failed request must never read as "nobody writes anything", and
              must never leave old figures looking freshly checked. */}
          {error && (
            <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
              {refusal ||
                (preview
                  ? "This could not be refreshed, so it may be out of date. Reload the page to try again."
                  : "This could not be loaded. Reload the page to try again.")}
            </div>
          )}
          {/* Said out loud, because a figure that leaves the peers out would
              otherwise read as a review with no peer work in it. */}
          {preview?.peerCountUnknown && (
            <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Peers are chosen after this {noun.toLowerCase()} starts, so their
              reviews are not counted here.
            </div>
          )}
          {preview ? (
            preview.writers.length > 0 ? (
              <LoadFigures
                preview={preview}
                noun={noun.toLowerCase()}
                seconds={secondsPerQuestion}
                onChangeSeconds={setSecondsPerQuestion}
              />
            ) : (
              // Peers left uncounted are the reason, when they are; the note
              // above says so, and this advice would be wrong.
              !preview.peerCountUnknown && (
                <div className="pt-3 text-sm text-gray-500">
                  Nobody has questions to answer yet. Turn on a step on the
                  Workflow tab and choose its questions on the Questions tab.
                </div>
              )
            )
          ) : (
            !error && (
              <div className="pt-3">
                <Loading size="5" mini />
              </div>
            )
          )}
        </>
      )}
      <ReadinessIntegrations
        organizationId={proposedComplianceProgram.organizationId}
      />
    </div>
  );
};

export default ProgramFormReadiness;
